/**
 * cart-bot-check.js
 * Bot detection for Shopify — works on cart page AND cart drawer.
 *
 * Flow:
 *   1. A global click listener waits for any checkout button to be clicked.
 *   2. The click is intercepted and the button is disabled.
 *   3. Cloudflare Turnstile runs (if a site key is configured).
 *   4. POST to WORKER_URL/verify-bot → receive signed HMAC token.
 *   5. Write bot_check={token} to the Shopify cart via /cart/update.js.
 *   6. Re-trigger the original checkout navigation.
 *
 * This approach only calls the Cloudflare Worker when the user actually
 * attempts checkout — no unnecessary traffic on regular page loads.
 *
 * If Turnstile is configured the invisible widget is pre-rendered on
 * DOMContentLoaded so it's ready to execute instantly on click.
 *
 * Configuration (injected by cart-bot-check.liquid via theme settings):
 *   window.BotCheck = {
 *     turnstileSiteKey: "0x...",                                      // Cloudflare Turnstile public site key
 *     workerUrl:        "https://third-act-bot-worker.workers.dev",   // Cloudflare Worker base URL
 *   };
 *
 * If turnstileSiteKey is empty, Turnstile is skipped (dev/test mode).
 * If workerUrl is empty the entire script exits silently.
 */

(function () {
  "use strict";

  // Guard: only initialise once per page.
  if (window.__botCheckInitialized) return;
  window.__botCheckInitialized = true;

  var cfg = window.BotCheck || {};
  var SITE_KEY = cfg.turnstileSiteKey || "";
  var WORKER_URL = (cfg.workerUrl || "").replace(/\/$/, "");

  if (!WORKER_URL) return;

  // Selector covering all checkout button shapes Shopify themes use.
  var CHECKOUT_SEL = [
    'button[name="checkout"]',
    'input[name="checkout"]',
    'a[href="/checkout"]',
    '.cart__checkout',
    'button[data-cart-checkout]'
  ].join(", ");

  // -------------------------------------------------------------------------
  // Helpers
  // -------------------------------------------------------------------------

  function showError() {
    var msg = document.getElementById("bot-check-error");
    if (msg) msg.style.display = "";
  }

  function setCartAttribute(key, value, callback) {
    var body = { attributes: {} };
    body.attributes[key] = value;
    fetch("/cart/update.js", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    })
      .then(function () { if (callback) callback(null); })
      .catch(function (err) {
        console.warn("[BotCheck] Failed to set cart attribute:", err);
        if (callback) callback(err);
      });
  }

  function fetchVerifyToken(turnstileToken, callback) {
    fetch(WORKER_URL + "/verify-bot", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ turnstileToken: turnstileToken || null }),
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (data && data.success && data.token) {
          callback(null, data.token);
        } else {
          callback(new Error(data && data.error ? data.error : "Verification failed"));
        }
      })
      .catch(function (err) {
        console.warn("[BotCheck] Backend call failed:", err);
        callback(err);
      });
  }

  // After a successful verify+cart-write, navigate to checkout.
  // We re-click the original button so Shopify's own handlers still fire.
  function proceedToCheckout(btn) {
    btn.setAttribute("data-bot-check-done", "1");
    btn.removeAttribute("disabled");
    btn.click();
  }

  function runVerification(btn, turnstileToken) {
    fetchVerifyToken(turnstileToken, function (err, token) {
      if (err) {
        console.warn("[BotCheck] Verification failed:", err);
        showError();
        btn.removeAttribute("disabled");
        return;
      }
      setCartAttribute("bot_check", token, function (cartErr) {
        if (cartErr) {
          // Cart write failed — allow checkout anyway to avoid blocking real users.
          console.warn("[BotCheck] Cart write failed, proceeding:", cartErr);
        }
        proceedToCheckout(btn);
      });
    });
  }

  // -------------------------------------------------------------------------
  // Turnstile (only when site key is configured)
  // -------------------------------------------------------------------------

  var turnstileWidgetId = null;
  var pendingBtn = null;

  function loadTurnstile() {
    if (document.getElementById("__bc-turnstile-script")) return;
    var script = document.createElement("script");
    script.id = "__bc-turnstile-script";
    // defer=false so it loads synchronously after DOM ready but before first click
    script.src = "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
    script.async = true;
    script.onload = function () {
      var container = document.createElement("div");
      container.id = "__bc-turnstile-widget";
      container.style.display = "none";
      document.body.appendChild(container);
      turnstileWidgetId = window.turnstile.render("#__bc-turnstile-widget", {
        sitekey: SITE_KEY,
        size: "invisible",
        execution: "execute",  // only run when turnstile.execute() is called
        callback: function (token) {
          if (pendingBtn) runVerification(pendingBtn, token);
          pendingBtn = null;
        },
        "error-callback": function () {
          console.warn("[BotCheck] Turnstile error");
          if (pendingBtn) {
            showError();
            pendingBtn.removeAttribute("disabled");
            pendingBtn = null;
          }
        },
      });
    };
    document.head.appendChild(script);
  }

  // -------------------------------------------------------------------------
  // Click intercept (capture phase — fires before Shopify's own handlers)
  // -------------------------------------------------------------------------

  document.addEventListener("click", function (e) {
    var btn = e.target.closest ? e.target.closest(CHECKOUT_SEL) : null;
    if (!btn) return;

    // Skip if we already verified on this element for this navigation.
    if (btn.getAttribute("data-bot-check-done")) {
      btn.removeAttribute("data-bot-check-done");
      return;
    }

    e.preventDefault();
    e.stopImmediatePropagation();
    btn.setAttribute("disabled", "disabled");

    if (SITE_KEY && window.turnstile && turnstileWidgetId !== null) {
      pendingBtn = btn;
      window.turnstile.reset(turnstileWidgetId);
      window.turnstile.execute(turnstileWidgetId);
    } else {
      // Dev mode or Turnstile not loaded yet — call Worker directly.
      runVerification(btn, null);
    }
  }, true); // capture phase

  // -------------------------------------------------------------------------
  // Initialisation: pre-load Turnstile so it's ready before first click
  // -------------------------------------------------------------------------

  if (SITE_KEY) {
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", loadTurnstile);
    } else {
      loadTurnstile();
    }
  }
})();
